import React, { useState } from 'react';

interface LazyImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  placeholder?: string;
  className?: string;
}

export default function LazyImage({ src, alt, placeholder = '/favicon.png', className = '', ...rest }: LazyImageProps) {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);

  const handleLoad = () => {
    setLoaded(true);
    // fade overlay then remove
    setTimeout(() => setShowOverlay(false), 350);
  };

  const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setErrored(true);
    setShowOverlay(false);
    (e.currentTarget as HTMLImageElement).style.display = 'none';
  };

  // Container style: show placeholder as background while loading or on error
  const containerStyle: React.CSSProperties = {
    backgroundImage: !loaded || errored ? `url(${placeholder})` : 'none',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  };

  return (
    <div className={`relative overflow-hidden ${className}`} style={containerStyle}>
      {/* Real image on top */}
      {!errored && (
        <img
          {...(rest as any)}
          src={String(src || '')}
          alt={alt}
          onLoad={handleLoad}
          onError={handleError}
          className={`w-full h-full object-cover transition-opacity duration-500 ease-in-out z-20 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        />
      )}

      {/* If errored, don't show real img; background placeholder will be visible with full opacity */}

      {/* Loading overlay with pulsing placeholder logo */}
      {showOverlay && !errored && (
        <div className={`absolute inset-0 flex items-center justify-center bg-white/20 backdrop-blur-sm z-30 transition-opacity duration-300 ${loaded ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
          <img
            src={placeholder}
            alt="placeholder"
            className="w-12 h-12 opacity-90 animate-pulse"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
          />
        </div>
      )}
    </div>
  );
}
